import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@archi/types/database.generated';

type Job = Database['public']['Tables']['render_jobs']['Row'];

export const useRenderJob = (jobId: string | undefined) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['render_job', jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<Job | null> => {
      if (!jobId) return null;
      const { data, error } = await supabase
        .from('render_jobs')
        .select('*')
        .eq('id', jobId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (!jobId) return;
    const channel = supabase
      .channel(`job:${jobId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'render_jobs', filter: `id=eq.${jobId}` },
        (payload) => {
          qc.setQueryData(['render_job', jobId], payload.new as Job);
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [jobId, qc]);

  return query;
};

export const useRenderList = (uid: string | undefined) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['renders', uid],
    enabled: !!uid,
    queryFn: async (): Promise<Job[]> => {
      if (!uid) return [];
      const { data, error } = await supabase
        .from('render_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!uid) return;
    const channel = supabase
      .channel(`renders:${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'render_jobs', filter: `user_id=eq.${uid}` },
        () => qc.invalidateQueries({ queryKey: ['renders', uid] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [uid, qc]);

  return query;
};
